/**
 * One breadcrumb chip in the FK Breadcrumb chip-row.
 *
 * Click the body → `onActivate(node.id)`. Click the × → `onDismiss(node.id)`.
 * Active chips are styled brighter; non-active (siblings of active path or
 * just inactive ancestors) render dimmer.
 *
 * Concern 4 of `goals/fk-breadcrumb-followup-1/GOAL.md` removed the prior
 * Slice-4 `branchSlot` (+N branch badge). Off-path siblings now render as
 * their own rows via `FkBreadcrumbTreeRow`, so the chip is a pure presenter
 * again.
 */

import type { KeyboardEvent } from 'react';
import { Pencil, X } from 'lucide-react';
import type { NavigationNode } from '../../../core/types/workspaceTypes';
import type { JoinTableHint } from '../../../single-row/utils/joinTableHintTypes';
import { chipLabel, chipTooltip } from './chipLabel';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '../../../../../components/ui/context-menu';

type FkBreadcrumbChipProps = {
  readonly node: NavigationNode;
  readonly isActive: boolean;
  /** True when the chip lives on the active path from root to active node. */
  readonly isOnActivePath: boolean;
  /**
   * True when this chip was just clicked and the heavy navigation update is
   * deferred to the next animation frame. The chip flips to "active" styling
   * immediately for snappy click feedback. No spinner — breadcrumb navigation
   * never refetches data, so a "loading" hint would be misleading. The chip
   * just appears already-selected and the data view catches up on the next
   * paint.
   */
  readonly isPendingActivate?: boolean;
  readonly onActivate: (id: string) => void;
  readonly onDismiss: (id: string) => void;
  /**
   * Slice 5 — when present and the node is a table-node, the chip's
   * right-click context menu surfaces MetaInfo `joinTableHint` overrides
   * ("Always show this table" / "Always collapse this table" / "Use auto").
   * `null` resets the override and falls back to the heuristic.
   */
  readonly onSetJoinTableHint?: (hint: JoinTableHint | null) => void;
};

export function FkBreadcrumbChip({
  node,
  isActive,
  isOnActivePath,
  isPendingActivate = false,
  onActivate,
  onDismiss,
  onSetJoinTableHint,
}: FkBreadcrumbChipProps) {
  // Tailwind classes derived statically so they can be tree-shaken.
  // Active chip = subtle amber pill (selected, with border + bg).
  // Non-active chips render as plain blue text — no border, no background —
  // so the active selection stands out and the rest of the trail stays
  // subtle. On-path chips get a stronger blue; off-path siblings are dimmer.
  const activeClass =
    'border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 hover:bg-amber-500/15';
  const onPathClass =
    'border border-transparent text-blue-700 dark:text-blue-300 hover:bg-blue-500/10';
  const inactiveClass =
    'border border-transparent text-blue-700/70 dark:text-blue-300/70 hover:bg-blue-500/10 hover:text-blue-700 dark:hover:text-blue-300';

  // Render the click target as already-active the instant the user clicks it,
  // even before the heavy store update lands. `isActive` (truth) and
  // `showActiveStyling` (visual) diverge only during the pending window.
  const showActiveStyling = isActive || isPendingActivate;
  const stateClass = showActiveStyling ? activeClass : isOnActivePath ? onPathClass : inactiveClass;
  const kindClass = node.kind === 'row' ? 'italic' : '';
  // Join-collapsed chips render dim so users can spot them once the
  // "Show join tables" toggle is on.
  const dimClass =
    node.kind === 'table' && node.isJoinCollapsed
      ? 'opacity-60 border-dashed'
      : '';

  // The whole pill — padding included — is the click target for activation.
  // Previously only the inner label `<button>` caught clicks, leaving the
  // pill's left padding visually-but-not-actually clickable. Using a
  // `role="button"` div lets us cover the full pill while still nesting the
  // dismiss `<button>` (nested real buttons would be invalid HTML).
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onActivate(node.id);
    }
  };

  // Edit-mode glyph: TableNodes that own active edit state (either toggled
  // on, or carrying unsaved pending changes) render a small blue pencil so
  // the user can see at a glance which breadcrumb nodes have edits to save.
  // Multiple chips can show the glyph simultaneously — each node owns its
  // own edit-state in the FK-breadcrumb follow-up.
  const isInEditMode =
    node.kind === 'table' &&
    ((node.isEditMode ?? false) || (node.pendingChanges?.length ?? 0) > 0);

  const chipBody = (
    <div
      role="button"
      tabIndex={0}
      className={`inline-flex items-center gap-1 pl-2 pr-0.5 py-0.5 rounded-full text-[11px] cursor-pointer focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-500/60 ${stateClass} ${kindClass} ${dimClass}`}
      onClick={() => onActivate(node.id)}
      onKeyDown={handleKeyDown}
      title={chipTooltip(node)}
      data-testid={`fk-breadcrumb-chip-${node.id}`}
      data-active={isActive ? 'true' : 'false'}
      data-pending-activate={isPendingActivate ? 'true' : 'false'}
      data-on-active-path={isOnActivePath ? 'true' : 'false'}
      data-node-kind={node.kind}
      data-join-collapsed={node.kind === 'table' && node.isJoinCollapsed ? 'true' : 'false'}
      data-edit-mode={isInEditMode ? 'true' : 'false'}
    >
      {isInEditMode && (
        <Pencil
          className="h-3 w-3 text-blue-500 dark:text-blue-400 flex-shrink-0"
          aria-label="Edit mode"
          data-testid="fk-chip-edit-glyph"
          data-edit-mode="true"
        />
      )}
      <span
        className="truncate max-w-[180px] pointer-events-none"
        data-testid={`fk-breadcrumb-chip-button-${node.id}`}
      >
        {chipLabel(node)}
      </span>
      <button
        type="button"
        className="inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-foreground/10 text-muted-foreground hover:text-foreground"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss(node.id);
        }}
        title="Dismiss"
        aria-label="Dismiss chip"
        data-testid={`fk-breadcrumb-chip-dismiss-${node.id}`}
      >
        <X className="h-3 w-3" aria-hidden="true" />
      </button>
    </div>
  );

  if (node.kind !== 'table' || !onSetJoinTableHint) {
    return chipBody;
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{chipBody}</ContextMenuTrigger>
      <ContextMenuContent data-testid={`fk-breadcrumb-chip-context-${node.id}`}>
        <ContextMenuItem
          className="text-xs"
          onClick={() => onSetJoinTableHint('expand')}
          data-testid="fk-breadcrumb-hint-expand"
        >
          Always show this table
        </ContextMenuItem>
        <ContextMenuItem
          className="text-xs"
          onClick={() => onSetJoinTableHint('collapse')}
          data-testid="fk-breadcrumb-hint-collapse"
        >
          Always collapse this table
        </ContextMenuItem>
        <ContextMenuItem
          className="text-xs"
          onClick={() => onSetJoinTableHint(null)}
          data-testid="fk-breadcrumb-hint-auto"
        >
          Use automatic detection
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
